// SSE consumer for POST /api/blog/v2/generate/stream.
//
// Same rationale as chatStream.ts: the browser-native EventSource cannot do a
// POST with a bearer header, so we do fetch() + a manual ReadableStream reader.
// The blog generate stream is small: `delta` (live writer tokens for the left
// pane) → `done` ({article_id}) once the article is persisted+linked → `error`.
import { apiUrl, authToken, ApiError } from "./api";

export interface BlogGenerateBody {
  topic_id: string;
  fmt?: string;
  use_story?: boolean | null;
}

export interface BlogStreamEvents {
  onDelta?: (text: string) => void;
  onDone?: (info: { article_id: string }) => void;
  onError?: (err: Error) => void;
}

/**
 * Streams a blog article generation. Returns a cancel() (AbortController) and a
 * done promise that resolves when the stream ends.
 */
export function streamBlogGenerate(
  body: BlogGenerateBody,
  events: BlogStreamEvents,
): { cancel: () => void; done: Promise<void> } {
  const controller = new AbortController();
  const done = (async () => {
    try {
      const tok = authToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (tok) headers["Authorization"] = `Bearer ${tok}`;

      const res = await fetch(apiUrl("/api/blog/v2/generate/stream"), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        let detail = `HTTP ${res.status}`;
        try {
          const text = await res.text();
          detail = text || detail;
        } catch {
          /* swallow */
        }
        throw new ApiError(res.status, detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buf += decoder.decode(value, { stream: true });
        let frameEnd: number;
        while ((frameEnd = buf.indexOf("\n\n")) !== -1) {
          const rawFrame = buf.slice(0, frameEnd);
          buf = buf.slice(frameEnd + 2);
          dispatch(rawFrame, events);
        }
      }
      if (buf.trim()) dispatch(buf, events);
    } catch (err) {
      if ((err as Error).name === "AbortError") return; // user cancel
      events.onError?.(err as Error);
    }
  })();

  return { cancel: () => controller.abort(), done };
}

function dispatch(rawFrame: string, events: BlogStreamEvents): void {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of rawFrame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return;
  let data: unknown = dataLines.join("\n");
  try {
    data = JSON.parse(data as string);
  } catch {
    /* keep as string */
  }
  switch (event) {
    case "delta":
      events.onDelta?.((data as { text?: string })?.text ?? "");
      return;
    case "done":
      events.onDone?.(data as { article_id: string });
      return;
    case "error":
      events.onError?.(new Error((data as { detail?: string })?.detail || "generation failed"));
      return;
    default:
      return;
  }
}
